import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";

export default function NewsletterForm() {
  const { t, prefix } = useLang();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((v) => ({ ...v, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const next = {};
    if (!values.name || values.name.trim().length < 2) next.name = t("newsletter.errorName");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!values.email || !emailRegex.test(values.email)) next.email = t("newsletter.errorEmail");
    if (!values.consent) next.consent = t("newsletter.errorConsent");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // Store lead locally (no backend). Redirect to Spanish thank you page.
    try {
      const leads = JSON.parse(localStorage.getItem("chasqui_leads") || "[]");
      leads.push({ ...values, at: new Date().toISOString() });
      localStorage.setItem("chasqui_leads", JSON.stringify(leads));
    } catch (e) {
      /* ignore */
    }
    navigate("/thank_you");
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="newsletter-form"
      className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="newsletter-name"
            className="block text-sm font-semibold text-slate-800"
          >
            {t("newsletter.nameLabel")}
          </label>
          <input
            id="newsletter-name"
            type="text"
            name="name"
            autoComplete="name"
            data-testid="newsletter-input-name"
            value={values.name}
            onChange={handleChange}
            placeholder={t("newsletter.namePlaceholder")}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20"
          />
          {errors.name && (
            <p data-testid="newsletter-error-name" className="mt-1 text-xs text-red-600">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="newsletter-email"
            className="block text-sm font-semibold text-slate-800"
          >
            {t("newsletter.emailLabel")}
          </label>
          <input
            id="newsletter-email"
            type="email"
            name="email"
            autoComplete="email"
            data-testid="newsletter-input-email"
            value={values.email}
            onChange={handleChange}
            placeholder={t("newsletter.emailPlaceholder")}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20"
          />
          {errors.email && (
            <p data-testid="newsletter-error-email" className="mt-1 text-xs text-red-600">
              {errors.email}
            </p>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            name="consent"
            data-testid="newsletter-input-consent"
            checked={values.consent}
            onChange={handleChange}
            className="mt-0.5 h-4 w-4 rounded border-slate-400 text-teal-600 focus:ring-teal-600"
          />
          <span>
            {t("newsletter.consent")}{" "}
            <Link
              to={`${prefix}/privacy`}
              data-testid="newsletter-privacy-link"
              className="text-teal-700 underline underline-offset-2 hover:text-teal-800"
            >
              {t("footer.links.privacy")}
            </Link>
            .
          </span>
        </label>
        {errors.consent && (
          <p data-testid="newsletter-error-consent" className="text-xs text-red-600">
            {errors.consent}
          </p>
        )}

        <button
          type="submit"
          data-testid="newsletter-submit-button"
          disabled={submitting}
          className="w-full rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {submitting ? t("newsletter.submitting") : t("newsletter.submit")}
        </button>

        <p className="text-xs text-slate-500">{t("newsletter.disclaimer")}</p>
      </div>
    </form>
  );
}
